import { createFileRoute, Link } from "@tanstack/react-router";
import heroDesktop from "@/assets/hero-motorhome-desktop.jpg.asset.json";
import heroMobile from "@/assets/hero-motorhome-mobile.jpg.asset.json";
import { AdSlot } from "@/components/AdSlot";

import { FyndanuPartnerStrip } from "@/components/FyndanuPartnerStrip";
import { FitTeaser } from "@/components/FitTeaser";
import { FyndanuGrid, FyndanuGridSkeleton, fyndanuListingsQuery } from "@/components/FyndanuGrid";
import { blocketVehiclesQuery } from "@/lib/vehicles";
import { listForumPosts } from "@/lib/forum.functions";
import { ForumTeaser } from "@/components/ForumTeaser";
import { FyndanuTrustRow } from "@/components/FyndanuTrustRow";
import { Suspense } from "react";
import { LiveViewers } from "@/components/LiveViewers";
import { SoldTodayBadge } from "@/components/SoldTodayBadge";

import { Reveal } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { StarBurst } from "@/components/StarBurst";
import interiorImg from "@/assets/interior.webp";
import campingImg from "@/assets/camping.webp";
import { ArrowRight, Award, MapPin, Phone, Sparkles, UserPlus, Wrench, MessageCircle, Camera, Bookmark, ShieldCheck, BadgeDollarSign, Banknote, Tent, Droplets, ScanLine } from "lucide-react";

const SELL_URL = "https://www.fyndanu.se/salj-husbil";


const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Var ligger AK Husbil & Fritid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Vi finns på Stabsvägen 5, 952 51 Kalix i Norrbotten. Ring 070-862 97 43 för att boka besök.",
      },
    },
    {
      "@type": "Question",
      name: "Säljer ni både husbilar och husvagnar?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Ja, vi har ett brett utbud av både husbilar och husvagnar. Vi säljer även tillbehör och utrustning för fritidslivet.",
      },
    },
    {
      "@type": "Question",
      name: "Är fordonen kontrollerade innan försäljning?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Ja, varje fordon kontrolleras innan det får plats hos oss. Vi står för det vi levererar och berättar både det bra och det som behöver göras.",
      },
    },
    {
      "@type": "Question",
      name: "Hur länge har ni funnits?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AK Husbil & Fritid är ett familjeägt företag som funnits i Kalix sedan 2004 — över 20 år i branschen.",
      },
    },
  ],
};

export const Route = createFileRoute("/")({
  // Förladda både Fyndanu- och Blocket-listan under SSR så hela lagret målas direkt
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(fyndanuListingsQuery).catch(() => null),
      context.queryClient.ensureQueryData(blocketVehiclesQuery).catch(() => null),
      listForumPosts({ data: { sort: "hot", limit: 6 } }).catch(() => null),
    ]).then(([, , forum]) => ({ forumPosts: forum?.posts ?? [] })),

  head: () => ({
    meta: [
      { title: "Husbilar & husvagnar i Kalix | AK Husbil & Fritid" },
      {
        name: "description",
        content:
          "Köp husbil eller husvagn i Kalix hos AK Husbil & Fritid. Familjeägt sedan 2004 — kontrollerade fordon, ärliga råd och personlig service. Ring 070-862 97 43.",
      },
      { name: "keywords", content: "husbil Kalix, husvagn Kalix, husbilshandlare Norrbotten, husvagnshandlare, fritidsfordon, AK Husbil & Fritid" },
      { property: "og:title", content: "AK Husbil & Fritid – Husbilar & husvagnar i Kalix" },
      {
        property: "og:description",
        content: "Familjeägd handlare i Kalix sedan 2004. Kontrollerade husbilar och husvagnar — trygg affär, personlig service.",
      },
      { property: "og:url", content: "https://www.akhusbilfritid.se/" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:width", content: "1216" },
      { property: "og:image:height", content: "640" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://www.akhusbilfritid.se/" },
      { rel: "alternate", hrefLang: "fi-FI", href: "https://www.akhusbilfritid.se/fi" },
      { rel: "alternate", hrefLang: "sv-SE", href: "https://www.akhusbilfritid.se/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://www.akhusbilfritid.se/" },
      // Responsiv preload — mobil får den lätta bilden, desktop den stora
      { rel: "preload", as: "image", href: heroMobile.url, media: "(max-width: 767px)", fetchPriority: "high" } as any,
      { rel: "preload", as: "image", href: heroDesktop.url, media: "(min-width: 768px)", fetchPriority: "high" } as any,
      // Varm DNS + TLS för Fyndanu-bilderna innan gridden renderas
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(FAQ_JSONLD) },
    ],
  }),
  component: HomePage,
});

const models = [
  { name: "Alkov", desc: "Originalfordonets förarhytt och dörrar. Ovanför förarplatsen finns en bekväm dubbelsäng — populär för barnfamiljer." },
  { name: "Delintegrerad", desc: "Bättre takhöjd att vistas i. Framstolarna går ofta att svänga runt vilket ger större användbar yta." },
  { name: "Helintegrerad", desc: "Originalbilens förarhytt ersatt av en helt integrerad förardel. Ofta med dubbelbädd som dras ned från taket." },
];

function HeroImage() {
  return (
    <picture>
      <source media="(max-width: 767px)" srcSet={heroMobile.url} />
      <img
        src={heroDesktop.url}
        alt="Modern husbil på kustväg vid solnedgång med snöklädda berg"
        decoding="async"
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover hero-kenburns"
      />
    </picture>
  );
}


function HomePage() {
  const { forumPosts } = Route.useLoaderData();
  return (
    <>
      {/* HERO — video background */}
      <section className="relative overflow-hidden bg-foreground grain">
        <div className="absolute inset-0">
          <HeroImage />
          {/* Gradient stack for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-foreground/40 via-foreground/25 to-foreground/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/65 via-transparent to-transparent" />
          <div className="pointer-events-none absolute inset-0 hero-vignette" />
        </div>

        <div className="container-edge relative flex min-h-[92vh] flex-col justify-end pb-20 pt-28 md:min-h-[96vh] md:pb-28 md:pt-32">
          <div className="max-w-3xl fade-up text-background">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-background/25 bg-background/5 px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-background/90 backdrop-blur-md">
                <Sparkles size={11} className="text-background/80" /> Rapido-återförsäljare · Officiell Fyndanu-butik · Sedan 2004
              </span>
              <LiveViewers />
              <SoldTodayBadge />
            </div>


            <h1 className="sr-only">AK Husbil & Fritid – Husbilar och husvagnar i Kalix sedan 2004</h1>
            <p aria-hidden="true" className="mt-7 font-display text-[3.25rem] leading-[0.92] tracking-tight text-background md:text-7xl lg:text-[5.75rem]">
              Din nästa<br />
              <span className="font-italic-display font-normal headline-accent">resa</span>
              <br />börjar här.
            </p>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-background/80 md:text-lg">
              Handplockade husbilar och husvagnar — varje fordon kontrolleras
              innan det får plats hos oss. Ärliga råd, trygg affär, inga
              säljknep.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a
                href={SELL_URL}
                target="_blank"
                rel="noopener"
                className="btn-cta btn-cta-accent group inline-flex w-full items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold shadow-[0_10px_40px_-10px_hsl(var(--accent)/0.7)] sm:w-auto"
              >
                <BadgeDollarSign size={16} /> Vi köper din husbil
                <ArrowRight size={16} className="transition group-hover:translate-x-1" />
              </a>
              <Link
                to="/fordon"
                className="btn-cta btn-cta-light group inline-flex w-full items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold sm:w-auto"
              >
                Se vårt utbud
                <ArrowRight size={16} className="transition group-hover:translate-x-1" />
              </Link>
              <Link
                to="/fukttest-husbil"
                hash="fuktskanner"
                aria-label="Öppna gratis AI-fuktskanner och fota en misstänkt fuktskada"
                className="group relative inline-flex w-full items-center justify-between gap-4 overflow-hidden rounded-full border border-accent/60 bg-foreground/55 px-5 py-2.5 text-left text-background shadow-[0_14px_44px_-18px_color-mix(in_oklab,var(--accent)_75%,transparent)] backdrop-blur-md transition hover:border-accent hover:bg-foreground/70 sm:w-auto sm:min-w-[16.5rem]"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-accent-foreground shadow-lg transition group-hover:scale-105">
                  <ScanLine size={17} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] font-medium uppercase tracking-[0.18em] text-background/65">Gratis AI-verktyg</span>
                  <span className="mt-0.5 block text-sm font-semibold leading-none">Skanna misstänkt fukt</span>
                </span>
                <ArrowRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-1" />
              </Link>
            </div>

          </div>

          <div className="mt-16 grid max-w-2xl grid-cols-3 gap-3 border-t border-background/15 pt-8 text-background sm:gap-6">
            {[
              { v: 100, suffix: "+", label: "Fordon i utbudet" },
              { v: 20, suffix: " år", label: "I branschen" },
              { v: 5, suffix: "★", label: "Kundbetyg", stars: true },
            ].map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div className="min-w-0">
                  <p className="font-display text-xl sm:text-2xl md:text-4xl">
                    {s.stars ? (
                      <StarBurst count={5} startDelay={300 + i * 80} stagger={130} />
                    ) : (
                      <Counter value={s.v} suffix={s.suffix} />
                    )}
                  </p>
                  <p className="mt-1 text-xs text-background/60 md:text-sm">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>




        </div>

        {/* scroll cue */}
        <div className="pointer-events-none absolute bottom-6 right-6 hidden items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-background/60 md:flex">
          <span className="h-px w-10 bg-background/40" /> Scrolla
        </div>
      </section>

      {/* PARTNER STRIP — officiell Fyndanu-butik */}
      <FyndanuPartnerStrip />

      {/* VÅRT LAGER — hela flödet direkt på startsidan */}
      <section id="vart-lager" className="border-t border-border bg-warm">
        <div className="container-edge py-14 md:py-20">
          <div className="mb-8 flex flex-col gap-4 md:mb-10 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="eyebrow text-accent">Vårt lager · live</span>
              <h2 className="mt-3 font-display text-4xl leading-[0.95] md:text-6xl">
                Hela vårt <span className="font-italic-display headline-accent">lager</span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-2 self-start md:self-auto">
              <Link
                to="/husbil-matchning"
                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground shadow-lg transition hover:shadow-accent/40"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                <span className="relative">✨ Matcha mig med rätt husbil</span>
                <ArrowRight size={16} className="relative" />
              </Link>
              <Link
                to="/fordon"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:bg-secondary"
              >
                Öppna fordonshallen <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <Suspense fallback={<FyndanuGridSkeleton />}>
            <FyndanuTrustRow />
            <FyndanuGrid initialLimit={9} rotateMs={90000} goldFrame hideTrustRow />
          </Suspense>

          <div className="mt-10 flex justify-center">
            <Link
              to="/campingtips"
              className="group inline-flex items-center gap-2.5 rounded-full border border-accent/40 bg-accent/10 px-6 py-3.5 text-sm font-semibold text-accent shadow-[0_8px_30px_-12px_hsl(var(--accent)/0.5)] transition hover:bg-accent hover:text-accent-foreground hover:shadow-accent/30"
            >
              <Tent size={18} />
              Upptäck våra campingtips i Norrbotten
              <ArrowRight size={16} className="transition group-hover:translate-x-1" />
            </Link>
          </div>

        </div>
      </section>

      {/* En enda annons på startsidan — efter lagret, före nästa sektion */}
      <div className="container-edge">
        <AdSlot variant="display-horizontal" label="Annons" />
      </div>







      {/* PASSAR DEN HOS MIG — interaktivt gratisverktyg */}
      <FitTeaser />

      {/* WE BUY — big CTA banner */}
      <section className="relative overflow-hidden bg-foreground text-background">
        <div className="pointer-events-none absolute inset-0 opacity-[0.15] [background:radial-gradient(circle_at_20%_30%,hsl(var(--accent))_0%,transparent_45%),radial-gradient(circle_at_80%_70%,hsl(var(--accent))_0%,transparent_40%)]" />
        <div className="container-edge relative py-14 md:py-20">
          <div className="flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
                <Banknote size={12} /> Vi köper in nu
              </span>
              <h2 className="mt-5 font-display text-4xl leading-[0.95] md:text-6xl">
                Sälj din husbil <span className="font-italic-display headline-accent">till oss</span>.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-background/75 md:text-lg">
                Vi köper in husbilar och husvagnar löpande — kostnadsfri värdering,
                snabb affär och ingen mellanhand. Fyll i formuläret så hör vi av oss.
              </p>
              <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-background/70">
                <li>✓ Kostnadsfri värdering</li>
                <li>✓ Svar inom 24 h</li>
                <li>✓ Vi hämtar i hela Sverige</li>
              </ul>
            </div>
            <div className="flex w-full flex-col gap-3 md:w-auto md:items-end">
              <a
                href={SELL_URL}
                target="_blank"
                rel="noopener"
                className="btn-cta btn-cta-accent group inline-flex w-full items-center justify-center gap-2 px-8 py-5 text-base font-semibold shadow-[0_20px_60px_-15px_hsl(var(--accent)/0.8)] md:w-auto md:text-lg"
              >
                <BadgeDollarSign size={20} /> Sälj din husbil nu
                <ArrowRight size={18} className="transition group-hover:translate-x-1" />
              </a>
              <a href="tel:0703045344" className="text-sm text-background/70 hover:text-background md:text-right">
                eller ring direkt: <span className="font-semibold text-background">070-304 53 44</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* RAPIDO DEALER — NEW 2026 */}
      <section className="relative border-b border-border bg-secondary/50">
        <div className="container-edge py-14 md:py-20">
          <div className="grid gap-8 md:grid-cols-12 md:items-start">
            <div className="md:col-span-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-accent">
                <Sparkles size={11} /> Nyhet 2026
              </span>
              <h2 className="mt-5 font-display text-4xl leading-[0.95] md:text-6xl">
                Officiell återförsäljare av{" "}
                <span className="font-italic-display headline-accent">Rapido</span>
              </h2>

              <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
                Vi är stolta att sedan i år representera Rapido — en av Europas
                mest hyllade husbilstillverkare. Fransk hantverkskvalitet,
                genomtänkta planlösningar och lång livslängd. Kom in och
                upplev dem på plats i Kalix.
              </p>

              {/* Award callout */}
              <div className="mt-6 flex items-start gap-4 rounded-2xl border border-[color:var(--gold-border,#c9a84c)]/40 bg-gradient-to-br from-[#fdf7e6] to-[#f7ecc9] p-4 shadow-[0_10px_30px_-15px_rgba(139,109,32,0.45)]">
                <div className="award-seal shrink-0">
                  <Award size={22} strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8b6d20]">
                    Owner Satisfaction Awards 2025
                  </p>
                  <p className="mt-1 font-display text-base leading-snug text-foreground md:text-lg">
                    Rapido utsedd till <em className="not-italic font-semibold">Best Manufacturer</em> — röstat av tusentals husbilsägare.
                  </p>
                </div>
              </div>

              {/* Incoming stock — 6 new Rapido on the way */}
              <div className="mt-4 flex items-start gap-4 rounded-2xl border border-accent/40 bg-accent/[0.06] p-4 shadow-[0_10px_30px_-18px_hsl(var(--accent)/0.6)]">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent/15 text-accent ring-1 ring-accent/30">
                  <span className="font-display text-lg leading-none">6</span>
                </div>
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-accent">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-70" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                    </span>
                    På ingång · Årsmodell 2026
                  </p>
                  <p className="mt-1 font-display text-base leading-snug text-foreground md:text-lg">
                    6 nya Rapido på väg in — <em className="not-italic font-semibold">förboka din innan de landar</em>.
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Ring oss nu för planlösningar, leveranstid och prisexempel.
                  </p>
                </div>
              </div>



              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="tel:0708629743"
                  className="btn-cta btn-cta-accent group inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold"
                >
                  <Phone size={15} className="transition group-hover:-rotate-12" /> Beställ ny Rapido — 070-862 97 43
                </a>
                <Link
                  to="/fordon"
                  className="btn-cta btn-cta-dark group inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold"
                >
                  Se Rapido hos oss
                  <ArrowRight size={16} className="transition group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/reservdelar"
                  className="btn-cta btn-cta-ghost group inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold"
                >
                  Rapido reservdelar
                  <ArrowRight size={16} className="transition group-hover:translate-x-1" />
                </Link>
              </div>


            </div>
            <div className="md:col-span-5">
              {/* Gold quality seal */}
              <div className="mb-4 flex justify-center overflow-hidden md:justify-end">
                <div className="quality-seal">
                  <div className="quality-seal-inner">
                    <span className="quality-seal-top">★ Award ★</span>
                    <span className="quality-seal-year">2025</span>
                    <span className="quality-seal-bot">Best Manufacturer</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 md:mt-[180px]">
                {[
                  { k: "Sedan", v: "1961" },
                  { k: "Ursprung", v: "Frankrike" },
                  { k: "Modeller", v: "Alkov · Del · Helintegrerad" },
                  { k: "Status", v: "Auktoriserad partner" },
                ].map((f) => (
                  <div key={f.k} className="rounded-2xl border border-border bg-background p-5">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-accent">{f.k}</p>
                    <p className="mt-2 font-display text-lg text-foreground">{f.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* WHY US */}

      <section className="container-edge py-24 md:py-32">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="eyebrow">Varför oss</p>
            <h2 className="mt-3 font-display text-4xl md:text-6xl">
              Trygghet i varje <span className="font-italic-display text-accent">affär</span>.
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-xl leading-relaxed text-foreground/90 md:text-2xl">
              Vi är ett familjeägt företag i Kalix — du pratar alltid med samma
              personer som tar hand om fordonen. Ingen mellanhand, inga
              överraskningar. Bara raka besked från någon som faktiskt kan.
            </p>
            <div className="mt-12 grid gap-5 sm:grid-cols-3">
              {[
                { title: "Kontrollerat", desc: "Varje fordon genomgås innan det säljs — vi står för det vi levererar." },
                { title: "Personligt", desc: "Du möts av en människa, inte ett säljscript. Ring direkt till oss." },
                { title: "Erfaret", desc: "20 år i branschen. Vi kan fordonen och vi kan vägen." },
              ].map((m, i) => (
                <Reveal key={m.title} delay={i * 120}>
                  <div className="rounded-2xl border border-border bg-card p-6 hover-lift">
                    <p className="font-display text-sm text-accent">0{i + 1}</p>
                    <p className="mt-3 text-lg font-semibold">{m.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                  </div>
                </Reveal>
              ))}

            </div>
          </div>
        </div>
      </section>


      {/* INTERIOR */}
      <section className="border-y border-border bg-secondary/40">
        <div className="container-edge grid gap-12 py-24 md:grid-cols-2 md:items-center md:py-32">
          <div className="img-frame aspect-[4/5] bg-card">
            <img src={interiorImg} alt="Mysig interiör i husbil" loading="lazy" className="h-full w-full object-cover" />
          </div>
          <div>
            <p className="eyebrow">Inifrån</p>
            <h2 className="mt-3 font-display text-4xl md:text-6xl">
              Ett hem<br />
              <span className="font-italic-display text-accent">på hjul</span>.
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground">
              Genomtänkt komfort, smarta förvaringslösningar och kvalitet som
              håller säsong efter säsong. Vi hjälper dig hitta utrustningen
              som gör skillnad på resan.
            </p>
            <Link
              to="/fritid"
              className="group mt-8 inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
            >
              Utforska fritid
              <ArrowRight size={16} className="transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* MODELS */}
      <section className="container-edge py-24 md:py-32">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="eyebrow">Modeller</p>
            <h2 className="mt-3 font-display text-4xl md:text-6xl">
              Välj din <span className="font-italic-display text-accent">form</span>.
            </h2>
          </div>
          <p className="max-w-md text-base text-muted-foreground">
            Osäker på vilken typ som passar dig? Ring oss — vi guidar utan
            press, oavsett om du är förstagångsköpare eller byter upp dig.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {models.map((m, i) => (
            <Reveal key={m.name} delay={i * 120}>
              <article className="group h-full rounded-3xl border border-border bg-card p-7 hover-lift">
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-5xl text-accent">0{i + 1}</span>
                  <ArrowRight size={20} className="text-muted-foreground transition group-hover:translate-x-1 group-hover:text-foreground" />
                </div>
                <h3 className="mt-8 font-display text-2xl">{m.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>

      </section>

      {/* MEMBERSHIP — gratis medlemskap */}
      <section className="relative border-y border-border bg-secondary/40">
        <div className="container-edge py-24 md:py-32">
          <div className="grid gap-12 md:grid-cols-12 md:items-start">
            <div className="md:col-span-5">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-accent">
                <UserPlus size={11} /> Gratis medlemskap
              </span>
              <h2 className="mt-5 font-display text-4xl leading-[0.95] md:text-6xl">
                Bli en del av vår <span className="font-italic-display headline-accent">community</span>.
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
                Skapa ett gratis konto och få allt på ett ställe — dina reservdelsförfrågningar
                autofylls, du kan dela egna campingtips och ställplatser, och du får
                överblick över alla dina ärenden hos oss.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/login"
                  className="btn-cta btn-cta-accent group inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold"
                >
                  Skapa konto — gratis
                  <ArrowRight size={16} className="transition group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/campingtips"
                  className="btn-cta btn-cta-ghost group inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold"
                >
                  Se campingtips
                  <ArrowRight size={16} className="transition group-hover:translate-x-1" />
                </Link>
              </div>
              <p className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck size={14} className="text-accent" /> Ingen bindningstid · Radera kontot när du vill
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  { icon: Wrench, title: "Autofyll reservdelar", desc: "Dina uppgifter fylls i automatiskt när du beställer reservdelar — snabbare hjälp." },
                  { icon: Bookmark, title: "Din historik", desc: "Se alla dina förfrågningar samlade — status, datum och vad du bad om." },
                  { icon: Camera, title: "Dela campingtips", desc: "Publicera egna ställplatser och campingar med bilder — hjälp andra husbilsägare." },
                  { icon: MessageCircle, title: "Kommentera bloggen", desc: "Ställ frågor och dela erfarenheter direkt under våra guider och tester." },
                ].map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <Reveal key={b.title} delay={i * 90}>
                      <div className="h-full rounded-2xl border border-border bg-background p-6 hover-lift">
                        <div className="grid h-11 w-11 place-items-center rounded-full bg-accent/10 text-accent ring-1 ring-accent/20">
                          <Icon size={18} />
                        </div>
                        <p className="mt-5 font-display text-lg">{b.title}</p>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.desc}</p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ForumTeaser posts={forumPosts as never} />

      {/* FUKTSKANNER — gratis AI-verktyg, starkaste egenvärdet vi har */}
      <section className="container-edge pt-20 md:pt-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-border bg-warm p-7 md:p-12">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-accent/10 blur-2xl"
            />
            <div className="relative grid gap-8 md:grid-cols-12 md:items-center">
              <div className="md:col-span-8">
                <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-accent">
                  <Droplets size={11} /> Gratis · svar direkt
                </span>
                <h2 className="mt-5 font-display text-3xl leading-[0.98] md:text-5xl">
                  Fota fläcken — få en{" "}
                  <span className="font-italic-display headline-accent">fuktbedömning</span> på en minut.
                </h2>
                <p className="mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
                  Misstänker du fukt i en husbil du äger eller är på väg att köpa? Ladda upp
                  1–4 bilder av området. Du får risknivå, trolig vattenkälla, exakta punkter
                  att sätta fuktmätaren i, åtgärder i rätt ordning och ett realistiskt
                  kostnadsspann — byggt på 20 år av öppnade väggar i vår verkstad i Kalix.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to="/fukttest-husbil"
                    hash="fuktskanner"
                    className="group inline-flex min-h-12 items-center gap-2 rounded-full bg-primary px-7 text-sm font-medium text-primary-foreground transition hover:opacity-90"
                  >
                    <ScanLine size={16} />
                    Skanna fuktskada gratis
                    <ArrowRight size={16} className="transition group-hover:translate-x-0.5" />
                  </Link>
                  <Link
                    to="/verktyg/fuktprotokoll"
                    className="inline-flex min-h-12 items-center gap-2 rounded-full border border-border px-6 text-sm text-muted-foreground transition hover:border-accent/50 hover:text-foreground"
                  >
                    Fuktprotokoll att skriva ut
                  </Link>
                </div>
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground md:col-span-4">
                {[
                  "Riskläge 0–4 med tydlig förklaring",
                  "Skiljer kondens från äkta inträngning",
                  "Spara rapporten och dela med säljaren",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <ShieldCheck size={16} className="mt-0.5 shrink-0 text-accent" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </section>

      {/*
        Intern länknav från startsidan. Nästan all inkommande auktoritet landar
        på "/" — här fördelas den vidare till lager, verktyg och kunskapssidor
        med beskrivande ankartext (ingen ny URL skapas).
      */}
      <section className="container-edge pb-8 pt-20 md:pt-28" aria-labelledby="hub-heading">
        <p className="eyebrow">Hitta rätt snabbt</p>
        <h2 id="hub-heading" className="mt-3 font-display text-3xl leading-tight md:text-5xl">
          Allt du behöver <span className="font-italic-display text-accent">före köpet</span>.
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Vårt lager, våra räknare och våra guider — samlat. Byggt på 20 år i verkstaden
          i Kalix, inte på gissningar.
        </p>

        <div className="mt-10 grid gap-8 md:grid-cols-3">
          {[
            {
              icon: Tent,
              title: "Lager & märken",
              links: [
                { to: "/fordon", label: "Alla husbilar & husvagnar till salu" },
                { to: "/husbilar-till-salu", label: "Begagnade husbilar i Kalix" },
                { to: "/husvagnar-till-salu", label: "Husvagnar till salu i Norrbotten" },
                { to: "/rapido", label: "Rapido – modellprogram & prislista" },
                { to: "/salda", label: "Sålda fordon med slutpris" },
              ],
            },
            {
              icon: BadgeDollarSign,
              title: "Marknad & värdering",
              links: [
                { to: "/marknad", label: "Marknadsrummet – all marknadsdata" },
                { to: "/prisindex", label: "AK Prisindex – prisutveckling per månad" },
                { to: "/vardera-husbil", label: "Värdera husbil eller husvagn gratis" },
                { to: "/prisstatistik-husbil", label: "Prisstatistik husbil 2026" },
                { to: "/borsen", label: "Fritidsbörsen – bolagen bakom märkena" },
              ],
            },

            {
              icon: ShieldCheck,
              title: "Kunskap från verkstaden",
              links: [
                { to: "/guider", label: "Alla köp- och skötselguider" },
                { to: "/fukttest-husbil", label: "Fukttest husbil – protokoll" },
                { to: "/b96-korkort", label: "B96-körkort: vad får du köra?" },
                { to: "/stallplatser-husbil", label: "Ställplatser för husbil i Sverige" },
                { to: "/forum", label: "Fritidsforum – frågor & erfarenheter" },
              ],
            },
          ].map((col, i) => {
            const Icon = col.icon;
            return (
              <Reveal key={col.title} delay={i * 90}>
                <div className="h-full rounded-2xl border border-border bg-card p-6">
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-accent/10 text-accent ring-1 ring-accent/20">
                      <Icon size={18} />
                    </span>
                    <p className="font-display text-lg">{col.title}</p>
                  </div>
                  <ul className="mt-4 space-y-1 text-sm md:mt-5">
                    {col.links.map((l) => (
                      <li key={l.to}>
                        <Link
                          to={l.to}
                          className="group flex min-h-11 w-full items-center gap-2 rounded-xl px-2 py-2.5 -mx-2 text-muted-foreground transition hover:bg-secondary/60 hover:text-foreground"
                        >
                          <ArrowRight
                            size={14}
                            className="shrink-0 text-accent transition group-hover:translate-x-0.5"
                          />
                          <span className="underline-offset-4 group-hover:underline">{l.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CTA */}

      <section className="container-edge pb-24 pt-24 md:pt-32">

        <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground elev-shadow">
          <img src={campingImg} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/60 to-primary/90" />
          <div className="relative px-8 py-20 md:px-16 md:py-28">
            <p className="eyebrow text-primary-foreground/70">Välkommen in</p>
            <h2 className="mt-3 font-display text-5xl leading-[0.95] md:text-7xl">
              Kom <span className="font-italic-display text-accent-foreground/90 italic" style={{ color: "oklch(0.85 0.10 30)" }}>förbi</span>.
            </h2>
            <p className="mt-5 max-w-md text-base text-primary-foreground/85 md:text-lg">
              Titta i lugn och ro, ställ alla frågor du har — vi bjuder på kaffet.
              Ingen tidsbokning behövs.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-primary-foreground/70">
              <MapPin size={16} /> Stabsvägen 5, Kalix
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="tel:0708629743"
                className="group inline-flex items-center gap-2 rounded-full bg-accent px-7 py-4 text-base font-semibold text-accent-foreground transition hover:opacity-95"
              >
                <Phone size={18} /> 070-862 97 43
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/25 bg-primary-foreground/5 px-7 py-4 text-base font-semibold text-primary-foreground transition hover:bg-primary-foreground/10"
              >
                Skicka meddelande
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
